import { useLanguage } from "@/lib/i18n";

export const Field = ({ label, required, error, hint, children }) => {
  const { t } = useLanguage();
  return (
    <div className="cp-field">
      {label && (
        <span className="cp-label">
          {t(label)}
          {required ? (
            <span className="cp-req" title="Required field" aria-hidden="true">
              {"\u00A0"}*
            </span>
          ) : (
            <span className="cp-opt"> {t("(optional)")}</span>
          )}
        </span>
      )}
      {children}
      {hint && !error && <p className="cp-hint">{t(hint)}</p>}
      {error && (
        <p className="cp-error" role="alert">
          {t(error)}
        </p>
      )}
    </div>
  );
};

export const TextInput = ({ label, required, error, hint, id, testId, type = "text", ...props }) => {
  const { t } = useLanguage();
  return (
    <Field label={label} required={required} error={error} hint={hint}>
      <input
        id={id}
        type={type}
        className={`cp-input${error ? " has-error" : ""}`}
        data-testid={testId || id}
        {...props}
        placeholder={props.placeholder ? t(props.placeholder) : undefined}
      />
    </Field>
  );
};

export const SelectField = ({
  label,
  required,
  error,
  hint,
  id,
  testId,
  options,
  placeholder = "Select",
  value,
  onChange,
}) => {
  const { t } = useLanguage();
  return (
    <Field label={label} required={required} error={error} hint={hint}>
      <select
        id={id}
        className={`cp-input cp-select${error ? " has-error" : ""}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId || id}
      >
        <option value="">{t(placeholder)}</option>
      {options.map((o) => {
        const val = typeof o === "object" ? o.value : o;
        const lab = typeof o === "object" ? o.label : o;
        return (
          <option key={val} value={val}>
            {lab}
          </option>
        );
      })}
    </select>
  </Field>
  );
};

export const TextAreaField = ({
  label,
  required,
  error,
  hint,
  id,
  testId,
  value,
  onChange,
  limit,
  rows = 4,
  placeholder,
}) => {
  const { t } = useLanguage();
  return (
    <Field label={label} required={required} error={error} hint={hint}>
      <textarea
        id={id}
        rows={rows}
        maxLength={limit}
        placeholder={placeholder ? t(placeholder) : undefined}
        className={`cp-input cp-textarea${error ? " has-error" : ""}`}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId || id}
      />
      {limit && (
        <span className="cp-counter" data-testid={`${testId || id}-counter`}>
          {(value || "").length}/{limit}
        </span>
      )}
    </Field>
  );
};

export const ChoiceGrid = ({ options, value, onChange, name, testIdPrefix, multiple = false }) => {
  const { t } = useLanguage();
  const isActive = (o) => (multiple ? value.includes(o) : value === o);
  return (
    <div className="cp-choice-grid" role={multiple ? "group" : "radiogroup"} aria-label={name}>
      {options.map((o) => (
        <button
          key={o}
          type="button"
          aria-pressed={isActive(o)}
          className={`cp-choice${isActive(o) ? " is-active" : ""}`}
          data-testid={`${testIdPrefix}-${o.toLowerCase().replace(/\s+/g, "-")}`}
          onClick={() => onChange(o)}
        >
          {t(o)}
        </button>
      ))}
    </div>
  );
};
